import { BACKEND_URL } from "./supabase.js";

export const BH = { "Content-Type": "application/json" };

// The admin credential is the signed session token minted at /api/ops/session
// (stored by the admin login). No hardcoded god-token in the bundle anymore.
const adminTok = () => (typeof localStorage !== "undefined" && localStorage.getItem("bloom_admin_tok")) || "";

// Signed store session token (minted at login, stored in bloom:sess) — the real,
// unforgeable seller auth. x-store-id is kept only as a legacy fallback until the
// backend flips STORE_ALLOW_LEGACY=off.
const sellerTok = () => { try { return JSON.parse(localStorage.getItem("bloom:sess") || "null")?.token || ""; } catch { return ""; } };

function storeHeaders(opts = {}) {
  const h = { ...BH };
  if (opts.storeId) h["x-store-id"] = opts.storeId;
  const t = sellerTok();
  if (t) h["Authorization"] = `Bearer ${t}`;
  if (opts.adminToken) h["x-admin-token"] = opts.adminToken;
  return h;
}

export const api = {
  async patchUser(id, data) {
    try {
      // x-store-id: id enforces that a store owner can only patch their own record
      const r = await fetch(`${BACKEND_URL}/api/user/${id}`, { method:"PATCH", headers:storeHeaders({ storeId: id }), body:JSON.stringify(data) });
      if (!r.ok) {
        let errText = "";
        try { const j = await r.json(); errText = j.error || JSON.stringify(j); } catch { errText = await r.text().catch(()=>""); }
        console.error("api.patchUser error:", r.status, errText);
        return errText || `HTTP ${r.status}`;
      }
      return true;
    } catch(e) { console.error("api.patchUser:", e); return e.message || "network error"; }
  },
  async insertUser(data) {
    let r, j;
    try {
      r = await fetch(`${BACKEND_URL}/api/user`, { method:"POST", headers:BH, body:JSON.stringify(data) });
      j = await r.json().catch(()=>({}));
    } catch(e) {
      console.error("api.insertUser network:", e);
      throw new Error("Couldn't reach the server. Check your connection and try again.");
    }
    if (!r.ok) {
      console.error("api.insertUser:", r.status, j);
      // Backend sends clean {error} strings for the actionable cases (duplicate
      // Instagram, disposable email, rate limit) — surface those to the seller. Raw
      // Supabase failures come back JSON-stringified; don't show those, use a fallback.
      const msg = (typeof j?.error === "string" && !j.error.trim().startsWith("{"))
        ? j.error : "Sign up failed. Please try again.";
      throw new Error(msg);
    }
    return j;
  },
  async insertProduct(data) {
    try {
      // Backend requires proof you own the store (data.user_id) this product is for.
      const r = await fetch(`${BACKEND_URL}/api/product`, { method:"POST", headers:storeHeaders({ storeId: data?.user_id }), body:JSON.stringify(data) });
      const j = await r.json();
      if (!r.ok) { console.error("api.insertProduct:", j); return null; }
      return j;
    } catch(e) { console.error("api.insertProduct:", e); return null; }
  },
  async patchProduct(id, data, opts = {}) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/product/${id}`, { method:"PATCH", headers:storeHeaders(opts), body:JSON.stringify(data) });
      if (!r.ok) { console.error("api.patchProduct:", r.status, await r.text()); return false; }
      return true;
    } catch(e) { console.error("api.patchProduct:", e); return false; }
  },
  async deleteProduct(id, opts = {}) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/product/${id}`, { method:"DELETE", headers:storeHeaders(opts) });
      return r.ok;
    } catch(e) { console.error("api.deleteProduct:", e); return false; }
  },
  async insertOrder(data) {
    // Retry once on a transient failure (network blip / cold connection) so customers
    // don't see "Could not create order" on a one-off hiccup at the start of checkout.
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const r = await fetch(`${BACKEND_URL}/api/order`, { method:"POST", headers:BH, body:JSON.stringify(data) });
        const j = await r.json();
        if (!r.ok) { console.error("api.insertOrder:", j); if (attempt === 2) return null; }
        else return j;
      } catch(e) { console.error("api.insertOrder:", e); if (attempt === 2) return null; }
      await new Promise(res => setTimeout(res, 500));
    }
    return null;
  },
  async patchOrder(id, data, opts = {}) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/order/${id}`, { method:"PATCH", headers:storeHeaders(opts), body:JSON.stringify(data) });
      return r.ok;
    } catch(e) { return false; }
  },
  async releaseStock(orderIds) {
    // Restore stock reserved by rzp-create-order when payment is cancelled/fails.
    // Releases STRICTLY by order id — the backend reads items from the order row and
    // CAS-guards the release, so a stale tab or replay can never inflate stock.
    // Accepts a single id or an array. Non-fatal — never throws.
    try {
      const ids = Array.isArray(orderIds) ? orderIds : (orderIds ? [orderIds] : []);
      if (!ids.length) return;
      await fetch(`${BACKEND_URL}/api/release-stock`, { method:"POST", headers:BH, body:JSON.stringify({ orderIds: ids }) });
    } catch(e) { /* non-fatal */ }
  },
  // Log an "added to cart" event so the seller can see abandoned carts. Non-fatal.
  trackCart(evt) {
    try { fetch(`${BACKEND_URL}/api/track-cart`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(evt) }).catch(()=>{}); } catch(e) { /* ignore */ }
  },
  async storeCartEvents(storeId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/analytics/cart/${storeId}`, { headers: storeHeaders({ storeId }) });
      if (!r.ok) return [];
      const d = await r.json();
      return Array.isArray(d?.items) ? d.items : [];
    } catch(e) { return []; }
  },
  async heldStock(storeId) {
    // Active checkout holds for a store → [{product_id, variation_name, held}].
    // Lets the storefront show "available = physical − holds". Non-fatal: returns []
    // on any error so the storefront silently falls back to physical stock.
    try {
      const r = await fetch(`${BACKEND_URL}/api/held-stock?storeId=${encodeURIComponent(storeId)}`, { headers: BH });
      if (!r.ok) return [];
      const j = await r.json();
      return Array.isArray(j.held) ? j.held : [];
    } catch(e) { return []; }
  },
  async heldStockAll() {
    // Active checkout holds across all stores → [{product_id, variation_name, held}].
    // Used by Marketplace to show "available = physical − holds" platform-wide.
    try {
      const r = await fetch(`${BACKEND_URL}/api/held-stock?all=1`, { headers: BH });
      if (!r.ok) return [];
      const j = await r.json();
      return Array.isArray(j.held) ? j.held : [];
    } catch(e) { return []; }
  },
  async insertCustomer(data) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/customer`, { method:"POST", headers:BH, body:JSON.stringify(data) });
      const j = await r.json();
      if (!r.ok) { console.error("api.insertCustomer:", j); return null; }
      return j;
    } catch(e) { console.error("api.insertCustomer:", e); return null; }
  },
  async findCustomerByEmail(email) {
    // IMPORTANT: throw on any non-OK / network failure — do NOT return []. A 429 (rate
    // limit) or a network blip means "we couldn't check", NOT "no account exists".
    // Returning [] here silently told real customers "you haven't signed up" and routed
    // them to signup (then a duplicate/insert failure) — blocking BOTH login and signup
    // for buyers behind shared carrier IPs (CGNAT) during high traffic. Callers must
    // distinguish "checked, none found" (returns []) from "couldn't check" (throws).
    const r = await fetch(`${BACKEND_URL}/api/customer/find?email=${encodeURIComponent(email)}`);
    if (!r.ok) { const err = new Error(`customer/find failed: ${r.status}`); err.status = r.status; throw err; }
    return await r.json();
  },
  async patchCustomer(id, data) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/customer/${id}`, { method:"PATCH", headers:{...BH,"x-customer-id":id}, body:JSON.stringify(data) });
      if (!r.ok) {
        let errText = "";
        try { const j = await r.json(); errText = j.error || JSON.stringify(j); } catch { errText = await r.text().catch(()=>""); }
        console.error("api.patchCustomer error:", r.status, errText);
        return errText || `HTTP ${r.status}`;
      }
      return true;
    } catch(e) { console.error("api.patchCustomer:", e); return e.message || "network error"; }
  },
  // Change a store's / customer's login email. Requires the current password
  // (verified server-side) + enforces uniqueness. role: "store" | "customer".
  async changeEmail({ id, password, newEmail, role }) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/change-email`, {
        method: "POST", headers: BH,
        body: JSON.stringify({ id, password, newEmail, role }),
      });
      const d = await r.json().catch(() => ({}));
      return r.ok ? { ok: true, email: d.email } : { ok: false, error: d.error || `HTTP ${r.status}` };
    } catch (e) { return { ok: false, error: e.message || "network error" }; }
  },
  // Change password while logged in. Verifies the current password server-side.
  async changePassword({ id, currentPassword, newPassword, role }) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: "POST", headers: BH,
        body: JSON.stringify({ id, currentPassword, newPassword, role }),
      });
      const d = await r.json().catch(() => ({}));
      return r.ok ? { ok: true } : { ok: false, error: d.error || `HTTP ${r.status}` };
    } catch (e) { return { ok: false, error: e.message || "network error" }; }
  },
  // Permanently delete the seller's own store. Self-authenticating via current password.
  async deleteStore({ id, password }) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/delete-store`, {
        method: "POST", headers: BH,
        body: JSON.stringify({ id, password }),
      });
      const d = await r.json().catch(() => ({}));
      return r.ok ? { ok: true } : { ok: false, error: d.error || `HTTP ${r.status}` };
    } catch (e) { return { ok: false, error: e.message || "network error" }; }
  },
  async getOrdersByStore(storeId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/orders/store/${storeId}`, { headers: storeHeaders({ storeId }) });
      if (!r.ok) { console.error("api.getOrdersByStore:", r.status); return []; }
      return await r.json();
    } catch(e) { console.error("api.getOrdersByStore:", e); return []; }
  },
  trackView(storeId) {
    // Fire-and-forget storefront view beacon for analytics. Never throws.
    try { fetch(`${BACKEND_URL}/api/track-view`, { method:"POST", headers:BH, body:JSON.stringify({ storeId }) }).catch(()=>{}); } catch(_) {}
  },
  async getAnalytics(storeId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/analytics/${storeId}`, { headers: storeHeaders({ storeId }) });
      if (!r.ok) return { views30: 0, byDay: {} };
      return await r.json();
    } catch(e) { return { views30: 0, byDay: {} }; }
  },
  async getAbandonedOrders(storeId) {
    // Cancelled/abandoned checkouts for this store (customers who reached payment but
    // didn't finish). Sends x-store-id so the backend authorises (PII).
    try {
      const r = await fetch(`${BACKEND_URL}/api/orders/abandoned/${storeId}`, { headers: storeHeaders({ storeId }) });
      if (!r.ok) { console.error("api.getAbandonedOrders:", r.status); return []; }
      return await r.json();
    } catch(e) { console.error("api.getAbandonedOrders:", e); return []; }
  },
  async getOrdersByCustomer(customerId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/orders/customer/${customerId}`, { headers: { ...BH, "x-customer-id": customerId } });
      if (!r.ok) { console.error("api.getOrdersByCustomer:", r.status); return []; }
      return await r.json();
    } catch(e) { console.error("api.getOrdersByCustomer:", e); return []; }
  },
  async insertPayout(data) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/payout`, { method:"POST", headers:{...BH,"x-admin-token": adminTok()}, body:JSON.stringify(data) });
      const j = await r.json();
      if (!r.ok) {
        const errMsg = j?.error || JSON.stringify(j);
        console.error("api.insertPayout failed:", r.status, errMsg, "payload:", data);
        return { __payoutError: true, status: r.status, message: errMsg };
      }
      return j;
    } catch(e) {
      console.error("api.insertPayout exception:", e);
      return { __payoutError: true, status: 0, message: e.message };
    }
  },
  async deletePayout(id) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/payout/${encodeURIComponent(id)}`, { method:"DELETE", headers:{...BH,"x-admin-token": adminTok()} });
      if (!r.ok) { const e = await r.json().catch(()=>{}); console.error("deletePayout failed:", r.status, e); return false; }
      return true;
    } catch(e) { console.error("deletePayout error:", e); return false; }
  },
  async deleteCustomer(id) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(id)}`, { method:"DELETE", headers:{...BH,"x-admin-token": adminTok()} });
      if (!r.ok) { const e = await r.json().catch(()=>{}); console.error("deleteCustomer failed:", r.status, e); return false; }
      return true;
    } catch(e) { console.error("deleteCustomer error:", e); return false; }
  },
  async getCustomers() {
    try {
      const r = await fetch(`${BACKEND_URL}/api/customers`, { headers:{ ...BH, "x-admin-token": adminTok() } });
      if (!r.ok) return null;
      return await r.json();
    } catch(e) { return null; }
  },
  async getPayouts() {
    try {
      const r = await fetch(`${BACKEND_URL}/api/payouts`, { headers:{ ...BH, "x-admin-token": adminTok() } });
      if (!r.ok) return null;
      return await r.json();
    } catch(e) { return null; }
  },
  async getStorePayouts(storeId) {
    try {
      // Seller reads its own payouts — prove ownership of storeId.
      const r = await fetch(`${BACKEND_URL}/api/payouts?storeId=${encodeURIComponent(storeId)}`, { headers:storeHeaders({ storeId }) });
      if (!r.ok) return [];
      return await r.json();
    } catch(e) { return []; }
  },
  async submitRestockRequest(storeId, productId, customerName, customerPhone, customerMessage, customerId = null, customerEmail = null, instagram = null) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/restock-request`, {
        method:"POST",
        headers:BH,
        body:JSON.stringify({ storeId, productId, customerName, customerPhone, customerMessage, customerId, customerEmail, instagram })
      });
      if (!r.ok) {
        let errText = "";
        try { const j = await r.json(); errText = j.error || JSON.stringify(j); } catch { errText = await r.text().catch(()=>""); }
        console.error("api.submitRestockRequest error:", r.status, errText);
        return { error: errText || `HTTP ${r.status}` };
      }
      return { success: true };
    } catch(e) { console.error("api.submitRestockRequest:", e); return { error: e.message || "network error" }; }
  },
  async getRestockRequests(storeId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/restock-requests/${encodeURIComponent(storeId)}`, { headers:BH });
      if (!r.ok) { console.error("api.getRestockRequests:", r.status); return []; }
      return await r.json();
    } catch(e) { console.error("api.getRestockRequests:", e); return []; }
  },
  async deleteRestockRequest(requestId) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/restock-request/${encodeURIComponent(requestId)}`, { method:"DELETE", headers:BH });
      if (!r.ok) { console.error("api.deleteRestockRequest:", r.status); return false; }
      return true;
    } catch(e) { console.error("api.deleteRestockRequest:", e); return false; }
  },
};
